/* Mock routes — rewards, gamification and missions. */

import type { Mission, RewardOffer } from '@core/types'
import { ApiError } from '../../http'
import { db, nextId } from '../store'
import { readString, type MockRoute } from '../types'
import { awardPoints } from './shared'

export const rewardRoutes: MockRoute[] = [
  {
    method: 'GET',
    pattern: '/rewards/account',
    handler: () => db.rewardAccount,
  },
  {
    method: 'GET',
    pattern: '/rewards/offers',
    handler: () => [...db.offers].sort((a, b) => a.costPoints - b.costPoints),
    emptyState: [] satisfies RewardOffer[],
  },
  {
    method: 'POST',
    pattern: '/rewards/offers/:id/redeem',
    handler: ({ params }) => {
      const offer = db.offers.find((item) => item.id === params.id)
      if (!offer) {
        throw new ApiError({
          status: 404,
          code: 'offer_not_found',
          message: 'That reward is no longer in the catalogue.',
        })
      }
      if (offer.redeemed) {
        throw new ApiError({
          status: 409,
          code: 'offer_already_redeemed',
          message: 'You have already redeemed this reward. Show the code at the partner counter.',
        })
      }
      if (db.rewardAccount.pointsBalance < offer.costPoints) {
        throw new ApiError({
          status: 409,
          code: 'insufficient_points',
          message: `You need ${offer.costPoints - db.rewardAccount.pointsBalance} more points for this reward. Completing a care task or a mission is the quickest way to earn them.`,
        })
      }

      offer.redeemed = true
      awardPoints(-offer.costPoints, `Redeemed: ${offer.title}`, 'redemption')
      db.activity.unshift({
        id: nextId('ac', 700),
        kind: 'redemption',
        title: 'Reward redeemed',
        detail: `${offer.title} · ${offer.partnerName}`,
        occurredAt: new Date().toISOString(),
        points: -offer.costPoints,
      })

      return {
        offer,
        account: db.rewardAccount,
        redemptionCode: `ENH-${offer.id.slice(-4).toUpperCase()}-${nextId('x', 1000).slice(-4)}`,
      }
    },
  },
  {
    method: 'GET',
    pattern: '/missions',
    handler: ({ query }) => {
      const status = query.get('status')
      const category = query.get('category')
      let missions = [...db.missions]
      if (status && status !== 'all') missions = missions.filter((item) => item.status === status)
      if (category && category !== 'all') {
        missions = missions.filter((item) => item.category === category)
      }
      return missions.sort(
        (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
      )
    },
    emptyState: [] satisfies Mission[],
  },
  {
    method: 'POST',
    pattern: '/missions/:id/join',
    handler: ({ params, body }) => {
      const mission = requireMission(params.id)
      if (mission.status === 'closed' || mission.status === 'completed') {
        throw new ApiError({
          status: 409,
          code: 'mission_closed',
          message: 'This mission is closed, so places can no longer be booked.',
        })
      }
      if (mission.participants >= mission.capacity) {
        throw new ApiError({
          status: 409,
          code: 'mission_full',
          message: 'All places for this mission are taken. Try another mission nearby.',
        })
      }
      const displayName = readString(body, 'displayName') ?? db.user.displayName
      mission.status = 'joined'
      mission.participants += 1
      return {
        mission,
        participant: { id: nextId('mp', 800), displayName, joinedAt: new Date().toISOString() },
      }
    },
  },
  {
    method: 'POST',
    pattern: '/missions/:id/submit',
    handler: ({ params, body }) => {
      const mission = requireMission(params.id)
      if (mission.status === 'closed' || mission.status === 'completed') {
        throw new ApiError({
          status: 409,
          code: 'mission_closed',
          message: 'This mission has already been reviewed and closed.',
        })
      }
      mission.status = 'submitted'
      const points = Math.round(mission.rewardPoints / 2)
      awardPoints(points, `Mission submitted — ${mission.title}`, 'mission')
      return {
        mission,
        proofLabel: readString(body, 'photoLabel') ?? 'mission-proof.jpg',
        pointsAwarded: points,
        note: `Half of the ${mission.rewardPoints} points are released now; the coordinator releases the rest when the proof is approved.`,
      }
    },
  },
]

function requireMission(id: string | undefined): Mission {
  const mission = db.missions.find((item) => item.id === id)
  if (!mission) {
    throw new ApiError({
      status: 404,
      code: 'mission_not_found',
      message: 'That mission no longer exists — it may have been removed by the organiser.',
    })
  }
  return mission
}
